import React from 'react';
import { Zap, CheckCircle, Globe, Palette, MessageCircle, Target } from 'lucide-react';

export default function NativeBranding() {
  const framework = [
    {
      phase: 'Phase 1: Cultural Intelligence',
      duration: '2-3 weeks',
      activities: [
        'Deep cultural research and consumer behavior analysis',
        'Local competitor positioning and messaging audit',
        'Language nuance identification beyond direct translation',
        'Regional values, aesthetics, and communication styles',
        'Localization opportunities vs. universal brand elements'
      ]
    },
    {
      phase: 'Phase 2: Brand Adaptation Strategy',
      duration: '3-4 weeks',
      activities: [
        'Brand positioning refinement for local resonance',
        'Messaging framework development in native language',
        'Visual identity adaptation (colors, imagery, design)',
        'Product naming and description localization',
        'Content strategy aligned with local platforms and formats'
      ]
    },
    {
      phase: 'Phase 3: Content Creation & Localization',
      duration: '4-6 weeks',
      activities: [
        'Website and e-commerce platform localization',
        'Marketing collateral creation by native creatives',
        'Social media content adapted for local platforms',
        'Video and photography featuring local models/settings',
        'Packaging and product materials localization'
      ]
    },
    {
      phase: 'Phase 4: Launch & Optimization',
      duration: 'Ongoing',
      activities: [
        'Local influencer and brand ambassador partnerships',
        'Community engagement and feedback integration',
        'A/B testing messaging and creative variations',
        'Performance analysis and cultural fit refinement',
        'Continuous optimization based on local insights'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'Beyond Translation',
      icon: MessageCircle,
      description: "Localization isn't about translating English to Spanish—it's about understanding how Mexican consumers think differently than Spanish consumers, even though they share a language. We work with native market specialists, not translation agencies."
    },
    {
      title: 'Native Creative Teams',
      icon: Palette,
      description: "Our creative partners live in the markets you're entering. They understand which colors convey trust, which messaging resonates emotionally, and which cultural references land—knowledge no foreign agency can replicate."
    },
    {
      title: 'Platform-Specific Strategy',
      icon: Globe,
      description: "Instagram dominates in the US. WeChat dominates in China. Mercado Libre in Latin America. We build brand presence on platforms that matter in each market, not force-fit your US playbook."
    },
    {
      title: 'Cultural Risk Mitigation',
      icon: Target,
      description: 'Cultural missteps can kill brands overnight. We pressure-test all brand elements—names, imagery, messaging—with local focus groups before launch to avoid expensive mistakes and brand damage.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Zap size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">Native Branding</h1>
              <p className="text-xl text-gray-600 mt-2">Build authentic local brand presence</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-4 text-[#000000]">Why Native Branding Matters</h2>
            <p className="text-lg text-gray-700 mb-4">
              Your brand succeeded in your home market because it resonates culturally. That same positioning might fall flat—or worse, offend—in new markets. Colors carry different meanings. Humor doesn't translate. Consumer values vary dramatically.
            </p>
            <p className="text-lg text-gray-700">
              Native branding means adapting your brand to feel local while maintaining core identity. It's the difference between being perceived as a foreign interloper versus a brand that "gets us." This cultural resonance drives trust, loyalty, and word-of-mouth growth that no amount of advertising can buy.
            </p>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Cultural Adaptation Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Successful market entry requires understanding not just what to say, but how to say it in ways that resonate with local cultural values, communication styles, and consumer expectations.
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Translation agencies localize words. We localize brands—the entire identity, positioning, and communication strategy adapted for cultural resonance while maintaining brand integrity.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Build Native Brand Presence?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss how to adapt your brand for authentic resonance in your target markets.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
